// client/src/pages/Home.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

/** Utility: short snippet from Quill HTML (first ~4 words) */
function getSnippetFromQuill(html = '') {
  const text = html.replace(/<[^>]+>/g, '').trim();
  if (!text) return '';
  const words = text.split(/\s+/);
  let snippet = words.slice(0, 4).join(' ');
  if (words.length > 4) snippet += '...';
  return snippet;
}

/** Build the full media URL from a relative path (like "uploads/123.png"). */
function getMediaUrl(path) {
  if (!path) return '/assets/images/placeholder.png';
  return `${process.env.REACT_APP_API_URL}/${path}`;
}

export default function Home() {
  const [homeConfig, setHomeConfig] = useState(null);

  // For react-slick
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 600,
    slidesToShow: 1,
    slidesToScroll: 1
  };

  // "Add to Cart" function for featured products
  const handleAddToCart = (product) => {
    let cart = JSON.parse(localStorage.getItem('cart')) || [];
    const existing = cart.find(item => item.id === product._id);
    if (existing) {
      existing.quantity++;
    } else {
      cart.push({
        id: product._id,
        name: product.name,
        price: product.price,
        image: product.images?.[0],
        quantity: 1
      });
    }
    localStorage.setItem('cart', JSON.stringify(cart));
    alert('Added to cart!');
    // Dispatch event for Navbar cart count
    window.dispatchEvent(new Event('cart-updated'));
  };

  // Fetch homeConfig from the server
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/home-config`)
      .then(res => setHomeConfig(res.data))
      .catch(err => console.error('Error fetching homeConfig:', err));
  }, []);

  // If data hasn't loaded yet
  if (!homeConfig) {
    return <div className="mt-16 p-4">Loading home page...</div>;
  }

  // Extract fields
  const {
    heroImage,
    heroTitle,
    heroSubtitle,
    heroTitleColor,
    heroSubtitleColor,
    heroButtonText,
    heroButtonColor,
    featuredProducts = [],
    featuredBlogs = []
  } = homeConfig;

  // Fallback hero image if none is set
  const heroImgUrl = heroImage
    ? getMediaUrl(heroImage)
    : '/assets/images/hero-banner.png';

  // Fallback text
  const title = heroTitle || 'Elevate Your Farming with Our Drones';
  const subtitle = heroSubtitle || 'Precision, efficiency & cost savings at your fingertips.';
  const titleColor = heroTitleColor || '#ffffff';
  const subtitleColor = heroSubtitleColor || '#ffffff';

  // For the hero button
  const buttonText = heroButtonText || 'Explore Products';
  const buttonBgColor = heroButtonColor || '#ffffff'; // background color
  // If you want to set a default text color for the button, you can add it here.

  return (
    <div>
      {/* Hero Section (using <img> for the hero banner) */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* The hero image as an <img> tag, absolutely positioned */}
        <img
          src={heroImgUrl}
          alt="Hero Banner"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black opacity-30"></div>

        {/* Animated hero text */}
        <motion.div
          className="relative z-10 text-center px-4"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <h1
            className="text-4xl md:text-6xl font-bold mb-4"
            style={{ color: titleColor }}
          >
            {title}
          </h1>
          <p
            className="text-xl md:text-2xl mb-6"
            style={{ color: subtitleColor }}
          >
            {subtitle}
          </p>
          <Link
            to="/shop"
            className="inline-block text-green-700 px-6 py-3 font-medium rounded-full 
                       hover:bg-gray-100 transition transform hover:-translate-y-1"
            style={{ backgroundColor: buttonBgColor }}
          >
            {buttonText}
          </Link>
        </motion.div>
      </section>

      {/* Featured Products */}
      <section className="max-w-7xl mx-auto py-16 px-4">
        <motion.h2
          className="text-3xl font-semibold text-center mb-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          Featured Products
        </motion.h2>

        {featuredProducts.length === 0 ? (
          <p className="text-center text-gray-600">No featured products selected.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
            {featuredProducts.map(product => {
              const snippet = getSnippetFromQuill(product.description || '');
              // Combine images + videos
              const media = [...(product.images || []), ...(product.videos || [])];

              return (
                <motion.div
                  key={product._id}
                  className="bg-white rounded-lg shadow-custom hover:shadow-xl transition flex flex-col overflow-hidden"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  {/* Media slider */}
                  <div className="w-full h-48">
                    {media.length > 1 ? (
                      <Slider {...sliderSettings}>
                        {media.map((m, idx) => {
                          const isVideo = /\.(mp4|mov|avi|webm)$/i.test(m);
                          const mediaUrl = getMediaUrl(m);
                          return (
                            <div key={idx}>
                              {isVideo ? (
                                <video
                                  src={mediaUrl}
                                  className="w-full h-48 object-cover"
                                  controls
                                />
                              ) : (
                                <img
                                  src={mediaUrl}
                                  alt="product-media"
                                  className="w-full h-48 object-cover"
                                />
                              )}
                            </div>
                          );
                        })}
                      </Slider>
                    ) : media.length === 1 ? (
                      // single media
                      /\.(mp4|mov|avi|webm)$/i.test(media[0]) ? (
                        <video
                          src={getMediaUrl(media[0])}
                          className="w-full h-48 object-cover"
                          controls
                        />
                      ) : (
                        <img
                          src={getMediaUrl(media[0])}
                          alt={product.name}
                          className="w-full h-48 object-cover"
                        />
                      )
                    ) : (
                      // none
                      <img
                        src="/assets/images/placeholder.png"
                        alt={product.name}
                        className="w-full h-48 object-cover"
                      />
                    )}
                  </div>

                  {/* Body */}
                  <Link to={`/products/${product._id}`} className="p-4 flex-grow">
                    <h3 className="text-lg font-semibold mb-2">
                      {product.name}
                    </h3>
                    <p className="text-primaryGreen font-bold text-xl">
                      ${product.price}
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      {snippet}
                    </p>
                  </Link>
                  <div className="p-4">
                    <button
                      onClick={() => handleAddToCart(product)}
                      className="w-full bg-secondaryGold text-black py-2 px-4 rounded-full 
                                 hover:bg-yellow-400 transition"
                    >
                      Add to Cart
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </section>

      {/* Featured Blogs */}
      <section className="max-w-7xl mx-auto py-16 px-4">
        <motion.h2
          className="text-3xl font-semibold text-center mb-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          Featured Blogs
        </motion.h2>

        {featuredBlogs.length === 0 ? (
          <p className="text-center text-gray-600">No featured blogs selected.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredBlogs.map(blog => {
              const snippet = getSnippetFromQuill(blog.content || '');
              const media = [...(blog.images || []), ...(blog.videos || [])];

              return (
                <motion.div
                  key={blog._id}
                  className="bg-white rounded shadow-custom flex flex-col overflow-hidden"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  {/* Media top */}
                  <div className="w-full h-48">
                    {media.length > 1 ? (
                      <Slider {...sliderSettings}>
                        {media.map((m, idx) => {
                          const isVideo = /\.(mp4|mov|avi|webm)$/i.test(m);
                          const mediaUrl = getMediaUrl(m);
                          return (
                            <div key={idx}>
                              {isVideo ? (
                                <video
                                  src={mediaUrl}
                                  className="w-full h-48 object-cover"
                                  controls
                                />
                              ) : (
                                <img
                                  src={mediaUrl}
                                  alt="blog-media"
                                  className="w-full h-48 object-cover"
                                />
                              )}
                            </div>
                          );
                        })}
                      </Slider>
                    ) : media.length === 1 ? (
                      /\.(mp4|mov|avi|webm)$/i.test(media[0]) ? (
                        <video
                          src={getMediaUrl(media[0])}
                          className="w-full h-48 object-cover"
                          controls
                        />
                      ) : (
                        <img
                          src={getMediaUrl(media[0])}
                          alt={blog.title}
                          className="w-full h-48 object-cover"
                        />
                      )
                    ) : (
                      <img
                        src="/assets/images/placeholder.png"
                        alt={blog.title}
                        className="w-full h-48 object-cover"
                      />
                    )}
                  </div>

                  {/* Body */}
                  <Link to={`/blogs/${blog._id}`} className="flex-grow p-4">
                    <h3 className="text-lg font-semibold mb-2">
                      {blog.title}
                    </h3>
                    <p className="text-sm text-gray-600">{snippet}</p>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
